import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { patientService, type Patient } from '../../../services/patientService';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import {
  surgeryService,
  type CreateSurgicalProcedureRequest,
  PROCEDURE_TYPE_LABELS,
  ANESTHESIA_LABELS,
  STATUS_LABELS,
} from '../surgeryService';

const SurgicalProcedureFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const doctorId = user?.doctor?.id ?? null;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');

  // Patient search
  const [patientSearch, setPatientSearch] = useState('');
  const [patientId, setPatientId] = useState<number | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const patientSearchRef = useRef<ReturnType<typeof setTimeout>>();

  // Form fields
  const [procedureDate, setProcedureDate] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [procedureType, setProcedureType] = useState<CreateSurgicalProcedureRequest['procedure_type']>('other');
  const [anesthesiaType, setAnesthesiaType] = useState<CreateSurgicalProcedureRequest['anesthesia_type']>(null);
  const [status, setStatus] = useState<'scheduled' | 'completed' | 'cancelled'>('scheduled');
  const [diagnosis, setDiagnosis] = useState('');
  const [technique, setTechnique] = useState('');
  const [findings, setFindings] = useState('');
  const [complications, setComplications] = useState('');
  const [postopIndications, setPostopIndications] = useState('');
  const [notes, setNotes] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');

  const handlePatientSearch = useCallback((term: string) => {
    setPatientSearch(term);
    setPatientId(null);
    clearTimeout(patientSearchRef.current);
    if (term.length < 2) { setPatients([]); return; }
    patientSearchRef.current = setTimeout(async () => {
      setSearchingPatients(true);
      try {
        const results = await patientService.search(term);
        setPatients(results);
      } catch { /* ignore */ } finally {
        setSearchingPatients(false);
      }
    }, 350);
  }, []);

  useEffect(() => {
    if (isEdit) return;
    const prePatientId = searchParams.get('patient_id');
    if (!prePatientId) return;
    patientService.getById(parseInt(prePatientId)).then((p) => {
      setPatientId(p.id);
      setPatientSearch(`${p.first_name} ${p.last_name} - ${p.identification ?? ''}`);
    }).catch(() => { void 0; });
  }, [isEdit, searchParams]);

  useEffect(() => {
    if (!isEdit) return;
    surgeryService.getById(Number(id)).then(proc => {
      setPatientId(proc.patient_id);
      setPatientSearch(`${proc.patient?.first_name ?? ''} ${proc.patient?.last_name ?? ''} - ${proc.patient?.identification ?? ''}`.trim());
      setProcedureDate(proc.procedure_date ? proc.procedure_date.slice(0, 10) : '');
      setProcedureName(proc.procedure_name);
      setProcedureType(proc.procedure_type);
      setAnesthesiaType(proc.anesthesia_type ?? null);
      setStatus(proc.status);
      setDiagnosis(proc.diagnosis);
      setTechnique(proc.technique ?? '');
      setFindings(proc.findings ?? '');
      setComplications(proc.complications ?? '');
      setPostopIndications(proc.postop_indications ?? '');
      setNotes(proc.notes ?? '');
      setDurationMinutes(proc.duration_minutes ?? '');
    }).catch(() => setError('Error cargando el procedimiento.')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patientId) { setError('Seleccione un paciente.'); return; }
    if (!doctorId) { setError('No hay doctor vinculado a su cuenta.'); return; }
    if (!procedureDate || !procedureName || !diagnosis) {
      setError('Complete los campos obligatorios: fecha, nombre del procedimiento y diagnóstico.');
      return;
    }

    setSaving(true);
    setError('');

    const payload: CreateSurgicalProcedureRequest = {
      patient_id: patientId,
      doctor_id: doctorId,
      procedure_date: procedureDate,
      procedure_name: procedureName,
      procedure_type: procedureType,
      anesthesia_type: anesthesiaType || null,
      status,
      diagnosis,
      technique: technique || null,
      findings: findings || null,
      complications: complications || null,
      postop_indications: postopIndications || null,
      notes: notes || null,
      duration_minutes: durationMinutes !== '' ? Number(durationMinutes) : null,
    };

    try {
      if (isEdit) {
        await surgeryService.update(Number(id), payload);
        navigate(`/surgery/${id}`);
      } else {
        const proc = await surgeryService.create(payload);
        navigate(`/surgery/${proc.id}`);
      }
    } catch {
      setError('Error al guardar el procedimiento. Verifique los datos.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner variant="full-page" text="Cargando..." size="lg" />;
  }

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{isEdit ? 'Editar Procedimiento' : 'Nuevo Procedimiento Quirúrgico'}</h3>
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Cirugías', to: '/surgery' }, { label: isEdit ? 'Editar' : 'Nuevo' }]} />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {error && (
          <div className="alert alert-danger d-flex align-items-center">
            <i className="fa fa-exclamation-circle me-2"></i> {error}
          </div>
        )}

        {/* Paciente y datos principales */}
        <div className="card mb-3">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0"><i className="fa fa-user me-2"></i>Paciente y Datos del Procedimiento</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3" style={{ position: 'relative' }}>
                <label className="form-label fw-semibold">Paciente *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar paciente por nombre o cédula..."
                  value={patientSearch}
                  onChange={e => handlePatientSearch(e.target.value)}
                  disabled={isEdit}
                />
                {searchingPatients && (
                  <small><Spinner size="sm" text="Buscando..." /></small>
                )}
                {patients.length > 0 && !patientId && (
                  <ul className="list-group" style={{ position: 'absolute', zIndex: 10, width: '100%', maxHeight: 200, overflowY: 'auto' }}>
                    {patients.map(p => (
                      <li
                        key={p.id}
                        className="list-group-item list-group-item-action"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setPatientId(p.id);
                          setPatientSearch(`${p.first_name} ${p.last_name} - ${p.identification}`);
                          setPatients([]);
                        }}
                      >
                        {p.first_name} {p.last_name} — {p.identification}
                      </li>
                    ))}
                  </ul>
                )}
                {patientId && !isEdit && (
                  <small className="text-success">
                    <i className="fa fa-check me-1"></i>Paciente seleccionado{' '}
                    <button type="button" className="btn btn-link btn-sm p-0 ms-1"
                      onClick={() => { setPatientId(null); setPatientSearch(''); }}>
                      Cambiar
                    </button>
                  </small>
                )}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Fecha del Procedimiento *</label>
                <input
                  type="date"
                  className="form-control"
                  value={procedureDate}
                  onChange={e => setProcedureDate(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Estado</label>
                <select className="form-select" value={status} onChange={e => setStatus(e.target.value as typeof status)}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Nombre del Procedimiento *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Colecistectomía laparoscópica"
                  value={procedureName}
                  onChange={e => setProcedureName(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Tipo de Procedimiento *</label>
                <select className="form-select" value={procedureType} onChange={e => setProcedureType(e.target.value as typeof procedureType)}>
                  {Object.entries(PROCEDURE_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Tipo de Anestesia</label>
                <select
                  className="form-select"
                  value={anesthesiaType ?? ''}
                  onChange={e => setAnesthesiaType((e.target.value || null) as typeof anesthesiaType)}
                >
                  <option value="">Sin especificar</option>
                  {Object.entries(ANESTHESIA_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold">Duración (minutos)</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Ej: 90"
                  min={1}
                  value={durationMinutes}
                  onChange={e => setDurationMinutes(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Diagnóstico y técnica */}
        <div className="card mb-3">
          <div className="card-header bg-secondary text-white">
            <h5 className="mb-0"><i className="fa fa-stethoscope me-2"></i>Diagnóstico y Técnica</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Diagnóstico *</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Diagnóstico preoperatorio..."
                  value={diagnosis}
                  onChange={e => setDiagnosis(e.target.value)}
                  required
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Técnica Quirúrgica</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Descripción de la técnica utilizada..."
                  value={technique}
                  onChange={e => setTechnique(e.target.value)}
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Hallazgos Intraoperatorios</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Hallazgos durante el procedimiento..."
                  value={findings}
                  onChange={e => setFindings(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Postoperatorio */}
        <div className="card mb-3">
          <div className="card-header" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
            <h5 className="mb-0"><i className="fa fa-heartbeat me-2"></i>Postoperatorio</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Complicaciones</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Complicaciones intra o postoperatorias (dejar vacío si ninguna)..."
                  value={complications}
                  onChange={e => setComplications(e.target.value)}
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Indicaciones Postoperatorias</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Indicaciones para el paciente tras el procedimiento..."
                  value={postopIndications}
                  onChange={e => setPostopIndications(e.target.value)}
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold">Notas Adicionales</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Notas adicionales..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-4">
          <Link to="/surgery" className="btn btn-outline-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving
              ? <><i className="fa fa-spinner fa-spin me-2"></i>Guardando...</>
              : <><i className="fa fa-save me-2"></i>{isEdit ? 'Actualizar Procedimiento' : 'Registrar Procedimiento'}</>
            }
          </button>
        </div>
      </form>
    </>
  );
};

export default SurgicalProcedureFormPage;
